const configuredApiBaseUrl = process.env.REACT_APP_ORDER_API_URL || "";
const isPlaceholderApiBaseUrl = configuredApiBaseUrl.includes("DEINE-HAMACHI-IP");
const isLocalApiHost = (() => {
  const hostname = window.location.hostname.toLowerCase();
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "::1" ||
    /^192\.168\.\d{1,3}\.\d{1,3}$/.test(hostname) ||
    /^10\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(hostname) ||
    /^172\.(1[6-9]|2\d|3[0-1])\.\d{1,3}\.\d{1,3}$/.test(hostname) ||
    /^25\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(hostname)
  );
})();
const API_BASE_URL =
  configuredApiBaseUrl && !isPlaceholderApiBaseUrl
    ? configuredApiBaseUrl
    : isLocalApiHost
      ? `${window.location.protocol}//${window.location.hostname}:4000`
      : "";
const SESSION_STORAGE_KEY = "mapartcraft_order_session";

const readStoredSession = () => {
  try {
    return JSON.parse(window.localStorage.getItem(SESSION_STORAGE_KEY));
  } catch (error) {
    return null;
  }
};

const writeStoredSession = (sessionData) => {
  window.localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(sessionData));
};

const clearStoredSession = () => {
  window.localStorage.removeItem(SESSION_STORAGE_KEY);
};

const request = async (path, { method = "GET", body, token } = {}) => {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || "Backend request failed");
  }

  return data;
};

const authenticatedFetch = async (path, options = {}) => {
  const storedSession = readStoredSession();
  if (!storedSession?.session?.token) {
    throw new Error("Bitte zuerst anmelden.");
  }

  return fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers: {
      ...(options.headers || {}),
      Authorization: `Bearer ${storedSession.session.token}`,
    },
  });
};

export const getStoredOrderSession = () => readStoredSession();

export const getOrderSettings = async () => request("/api/settings");

export const getAuthCaptcha = async () => request("/api/auth/captcha");

export const registerOrderUser = async ({ playerName, password, turnstileToken }) => {
  const data = await request("/api/auth/register", {
    method: "POST",
    body: { playerName, password, turnstileToken },
  });
  writeStoredSession(data);
  return data;
};

export const loginOrderUser = async ({ playerName, password, twoFactorCode }) => {
  const data = await request("/api/auth/login", {
    method: "POST",
    body: { playerName, password, twoFactorCode },
  });
  writeStoredSession(data);
  return data;
};

export const createTwoFactorSetup = async () => {
  const storedSession = readStoredSession();
  if (!storedSession?.session?.token) throw new Error("Bitte zuerst anmelden.");

  return request("/api/auth/2fa/setup", {
    method: "POST",
    token: storedSession.session.token,
  });
};

export const enableTwoFactor = async ({ code }) => {
  const storedSession = readStoredSession();
  if (!storedSession?.session?.token) throw new Error("Bitte zuerst anmelden.");

  const data = await request("/api/auth/2fa/enable", {
    method: "POST",
    token: storedSession.session.token,
    body: { code },
  });
  writeStoredSession({ ...storedSession, user: data.user });
  return data;
};

export const disableTwoFactor = async ({ code }) => {
  const storedSession = readStoredSession();
  if (!storedSession?.session?.token) throw new Error("Bitte zuerst anmelden.");

  const data = await request("/api/auth/2fa/disable", {
    method: "POST",
    token: storedSession.session.token,
    body: { code },
  });
  writeStoredSession({ ...storedSession, user: data.user });
  return data;
};

export const getMyUserProfile = async () => {
  const storedSession = readStoredSession();
  if (!storedSession?.session?.token) throw new Error("Bitte zuerst anmelden.");

  return request("/api/users/me", {
    token: storedSession.session.token,
  });
};

export const updateMyUserProfile = async ({ profileText, themePreference }) => {
  const storedSession = readStoredSession();
  if (!storedSession?.session?.token) throw new Error("Bitte zuerst anmelden.");

  const body = { profileText };
  if (themePreference !== undefined) body.themePreference = themePreference;

  const data = await request("/api/users/me", {
    method: "PATCH",
    token: storedSession.session.token,
    body,
  });
  writeStoredSession({ ...storedSession, user: data.user });
  return data;
};

export const changeMyUserPassword = async ({ currentPassword, newPassword }) => {
  const storedSession = readStoredSession();
  if (!storedSession?.session?.token) throw new Error("Bitte zuerst anmelden.");

  const passwordChangeRequest = {
    method: "POST",
    token: storedSession.session.token,
    body: { currentPassword, newPassword },
  };

  try {
    return await request("/api/users/me/password", passwordChangeRequest);
  } catch (error) {
    if (error.message !== "Not found") throw error;
    return request("/api/auth/change-password", passwordChangeRequest);
  }
};

export const uploadMyUserAvatar = async ({ file }) => {
  const storedSession = readStoredSession();
  if (!storedSession?.session?.token) throw new Error("Bitte zuerst anmelden.");

  const formData = new FormData();
  formData.append("file", file);
  const response = await authenticatedFetch("/api/users/me/avatar", {
    method: "POST",
    body: formData,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || "Profilbild konnte nicht hochgeladen werden.");
  writeStoredSession({ ...storedSession, user: data.user });
  return data;
};

export const getMyUserAvatarObjectUrl = async () => {
  const response = await authenticatedFetch("/api/users/me/avatar");
  if (!response.ok) return "";
  const blob = await response.blob();
  return URL.createObjectURL(blob);
};

export const listOrderUsers = async ({ search = "" } = {}) => {
  const storedSession = readStoredSession();
  const params = new URLSearchParams();
  if (search) params.set("search", search);
  const query = params.toString() ? `?${params.toString()}` : "";

  if (storedSession?.session?.token) {
    try {
      return await request(`/api/users${query}`, {
        token: storedSession.session.token,
      });
    } catch (error) {
      if (error.message !== "Not found") throw error;
    }
  }

  try {
    return await request(`/api/public/users${query}`);
  } catch (error) {
    if (error.message === "Not found") {
      throw new Error("Nutzerliste noch nicht im laufenden Backend aktiv. Bitte Backend neu starten und danach erneut anmelden.");
    }
    throw error;
  }
};

export const getOrderUserAvatarObjectUrl = async ({ userId }) => {
  const storedSession = readStoredSession();
  const privateAvatarResponse = storedSession?.session?.token
    ? await fetch(`${API_BASE_URL}/api/users/${userId}/avatar`, {
        headers: { Authorization: `Bearer ${storedSession.session.token}` },
      }).catch(() => null)
    : null;
  const response =
    privateAvatarResponse && privateAvatarResponse.ok
      ? privateAvatarResponse
      : await fetch(`${API_BASE_URL}/api/public/users/${userId}/avatar`);
  if (!response.ok) return "";
  const blob = await response.blob();
  return URL.createObjectURL(blob);
};

export const sendDirectMessageToUser = async ({ userId, body }) => {
  const storedSession = readStoredSession();
  if (!storedSession?.session?.token) throw new Error("Bitte zuerst anmelden.");

  return request(`/api/users/${userId}/direct-messages`, {
    method: "POST",
    token: storedSession.session.token,
    body: { body },
  });
};

export const listMyDirectMessages = async ({ box = "inbox" } = {}) => {
  const storedSession = readStoredSession();
  if (!storedSession?.session?.token) throw new Error("Bitte zuerst anmelden.");

  const params = new URLSearchParams();
  params.set("box", box);
  return request(`/api/users/me/direct-messages?${params.toString()}`, {
    token: storedSession.session.token,
  });
};

export const markMyDirectMessageRead = async ({ messageId }) => {
  const storedSession = readStoredSession();
  if (!storedSession?.session?.token) throw new Error("Bitte zuerst anmelden.");

  return request(`/api/users/me/direct-messages/${messageId}/read`, {
    method: "POST",
    token: storedSession.session.token,
  });
};

export const createFeedbackEntry = async ({ feedback, attachment }) => {
  const formData = new FormData();
  Object.entries(feedback || {}).forEach(([key, value]) => {
    formData.append(key, value == null ? "" : value);
  });
  if (attachment) formData.append("attachment", attachment);

  const response = await authenticatedFetch("/api/feedback", {
    method: "POST",
    body: formData,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || "Feedback konnte nicht eingereicht werden.");
  return data;
};

export const listMyFeedbackEntries = async () => {
  const storedSession = readStoredSession();
  if (!storedSession?.session?.token) throw new Error("Bitte zuerst anmelden.");

  return request("/api/feedback/mine", {
    token: storedSession.session.token,
  });
};

export const listMyAchievements = async () => {
  const storedSession = readStoredSession();
  if (!storedSession?.session?.token) throw new Error("Bitte zuerst anmelden.");

  return request("/api/users/me/achievements", {
    token: storedSession.session.token,
  });
};

export const unlockMyAchievement = async ({ achievementId, metadata }) => {
  const storedSession = readStoredSession();
  if (!storedSession?.session?.token) throw new Error("Bitte zuerst anmelden.");

  return request("/api/users/me/achievements", {
    method: "POST",
    token: storedSession.session.token,
    body: { achievementId, metadata },
  });
};

export const createDiscordLinkCode = async () => {
  const storedSession = readStoredSession();
  if (!storedSession?.session?.token) throw new Error("Bitte zuerst anmelden.");

  return request("/api/users/me/discord-link-code", {
    method: "POST",
    token: storedSession.session.token,
  });
};

export const getOpsuchtVerification = async () => {
  const storedSession = readStoredSession();
  if (!storedSession?.session?.token) throw new Error("Bitte zuerst anmelden.");

  return request("/api/users/me/opsucht-verification", {
    token: storedSession.session.token,
  });
};

export const createOpsuchtVerificationChallenge = async () => {
  const storedSession = readStoredSession();
  if (!storedSession?.session?.token) throw new Error("Bitte zuerst anmelden.");

  return request("/api/users/me/opsucht-verification", {
    method: "POST",
    token: storedSession.session.token,
  });
};

export const checkOpsuchtVerification = async () => {
  const storedSession = readStoredSession();
  if (!storedSession?.session?.token) throw new Error("Bitte zuerst anmelden.");

  return request("/api/users/me/opsucht-verification/check", {
    method: "POST",
    token: storedSession.session.token,
  });
};

export const prepareNameChangeCheck = async () => {
  const storedSession = readStoredSession();
  if (!storedSession?.session?.token) throw new Error("Bitte zuerst anmelden.");

  return request("/api/users/me/name-change-check", {
    method: "POST",
    token: storedSession.session.token,
  });
};

export const getCurrentOrderUser = async () => {
  const storedSession = readStoredSession();
  if (!storedSession?.session?.token) return null;

  const data = await request("/api/auth/me", {
    token: storedSession.session.token,
  });
  return {
    ...storedSession,
    user: data.user,
  };
};

export const logoutOrderUser = async () => {
  const storedSession = readStoredSession();
  clearStoredSession();

  if (storedSession?.session?.token) {
    await request("/api/auth/logout", {
      method: "POST",
      token: storedSession.session.token,
    }).catch(() => null);
  }
};

export const createMapCompany = async ({ name, description }) => {
  const storedSession = readStoredSession();
  if (!storedSession?.session?.token) {
    throw new Error("Bitte zuerst anmelden.");
  }

  return request("/api/organizations", {
    method: "POST",
    token: storedSession.session.token,
    body: { name, description },
  });
};

export const listMyMapCompanies = async () => {
  const storedSession = readStoredSession();
  if (!storedSession?.session?.token) return { organizations: [] };

  return request("/api/organizations/mine", {
    token: storedSession.session.token,
  });
};

export const listPublicMapCompanies = async () => request("/api/organizations");

export const listMyMapCompanyJoinRequests = async () => {
  const storedSession = readStoredSession();
  if (!storedSession?.session?.token) return { joinRequests: [] };

  return request("/api/organizations/join-requests/mine", {
    token: storedSession.session.token,
  });
};

export const requestMapCompanyMembership = async ({ slug, message }) => {
  const storedSession = readStoredSession();
  if (!storedSession?.session?.token) {
    throw new Error("Bitte zuerst anmelden.");
  }

  return request(`/api/organizations/${slug}/join-requests`, {
    method: "POST",
    token: storedSession.session.token,
    body: { message },
  });
};

export const updateMapCompanyProfile = async ({ slug, profile }) => {
  const storedSession = readStoredSession();
  if (!storedSession?.session?.token) {
    throw new Error("Bitte zuerst anmelden.");
  }

  return request(`/api/organizations/${slug}`, {
    method: "PATCH",
    token: storedSession.session.token,
    body: profile,
  });
};

export const deleteMapCompany = async ({ slug }) => {
  const storedSession = readStoredSession();
  if (!storedSession?.session?.token) {
    throw new Error("Bitte zuerst anmelden.");
  }

  return request(`/api/organizations/${slug}`, {
    method: "DELETE",
    token: storedSession.session.token,
  });
};

export const listMapCompanyMembers = async ({ slug }) => {
  const storedSession = readStoredSession();
  if (!storedSession?.session?.token) {
    throw new Error("Bitte zuerst anmelden.");
  }

  return request(`/api/organizations/${slug}/members`, {
    token: storedSession.session.token,
  });
};

export const listMapCompanyJoinRequests = async ({ slug }) => {
  const storedSession = readStoredSession();
  if (!storedSession?.session?.token) {
    throw new Error("Bitte zuerst anmelden.");
  }

  return request(`/api/organizations/${slug}/join-requests`, {
    token: storedSession.session.token,
  });
};

export const resolveMapCompanyJoinRequest = async ({ slug, requestId, status }) => {
  const storedSession = readStoredSession();
  if (!storedSession?.session?.token) {
    throw new Error("Bitte zuerst anmelden.");
  }

  return request(`/api/organizations/${slug}/join-requests/${requestId}`, {
    method: "PATCH",
    token: storedSession.session.token,
    body: { status },
  });
};

export const addMapCompanyMember = async ({ slug, playerName, role }) => {
  const storedSession = readStoredSession();
  if (!storedSession?.session?.token) {
    throw new Error("Bitte zuerst anmelden.");
  }

  return request(`/api/organizations/${slug}/members`, {
    method: "POST",
    token: storedSession.session.token,
    body: { playerName, role },
  });
};

export const updateMapCompanyMember = async ({ slug, memberId, role, active }) => {
  const storedSession = readStoredSession();
  if (!storedSession?.session?.token) {
    throw new Error("Bitte zuerst anmelden.");
  }

  return request(`/api/organizations/${slug}/members/${memberId}`, {
    method: "PATCH",
    token: storedSession.session.token,
    body: { role, active },
  });
};

export const getMapCompanyDiscordSettings = async ({ slug }) => {
  const storedSession = readStoredSession();
  if (!storedSession?.session?.token) {
    throw new Error("Bitte zuerst anmelden.");
  }

  return request(`/api/organizations/${slug}/discord-settings`, {
    token: storedSession.session.token,
  });
};

export const updateMapCompanyDiscordSettings = async ({ slug, settings }) => {
  const storedSession = readStoredSession();
  if (!storedSession?.session?.token) {
    throw new Error("Bitte zuerst anmelden.");
  }

  return request(`/api/organizations/${slug}/discord-settings`, {
    method: "PATCH",
    token: storedSession.session.token,
    body: settings,
  });
};

export const listMapCompanyOrderTypes = async ({ slug }) => {
  const storedSession = readStoredSession();
  if (!storedSession?.session?.token) {
    throw new Error("Bitte zuerst anmelden.");
  }

  return request(`/api/organizations/${slug}/order-types`, {
    token: storedSession.session.token,
  });
};

export const uploadMapCompanyLogo = async ({ slug, file }) => {
  const formData = new FormData();
  formData.append("file", file);
  const response = await authenticatedFetch(`/api/organizations/${slug}/logo`, {
    method: "POST",
    body: formData,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || "Firmenlogo konnte nicht hochgeladen werden.");
  return data;
};

export const getMapCompanyLogoUrl = ({ slug }) => `${API_BASE_URL}/api/organizations/${slug}/logo`;

export const getMyMapCompanyLogoUrl = ({ slug }) => `${API_BASE_URL}/api/organizations/${slug}/logo/mine`;

export const createMapCompanyDefaultOrderTypes = async ({ slug }) => {
  const storedSession = readStoredSession();
  if (!storedSession?.session?.token) {
    throw new Error("Bitte zuerst anmelden.");
  }

  return request(`/api/organizations/${slug}/order-types/defaults`, {
    method: "POST",
    token: storedSession.session.token,
  });
};

export const updateMapCompanyOrderType = async ({ slug, orderTypeId, update }) => {
  const storedSession = readStoredSession();
  if (!storedSession?.session?.token) {
    throw new Error("Bitte zuerst anmelden.");
  }

  return request(`/api/organizations/${slug}/order-types/${orderTypeId}`, {
    method: "PATCH",
    token: storedSession.session.token,
    body: update,
  });
};

export const findMatchingMapCompanies = async ({ category, variant, mapCount, budget }) => {
  const storedSession = readStoredSession();
  if (!storedSession?.session?.token) {
    throw new Error("Bitte zuerst anmelden.");
  }

  return request("/api/orders/matching-organizations", {
    method: "POST",
    token: storedSession.session.token,
    body: { category, variant, mapCount, budget },
  });
};

export const createMapOrder = async ({ organizationSlug, orderTypeId, variant, mapCount, budget, notes, project }) => {
  const storedSession = readStoredSession();
  if (!storedSession?.session?.token) {
    throw new Error("Bitte zuerst anmelden.");
  }

  return request("/api/orders", {
    method: "POST",
    token: storedSession.session.token,
    body: { organizationSlug, orderTypeId, variant, mapCount, budget, notes, project },
  });
};

export const listMyMapOrders = async () => {
  const storedSession = readStoredSession();
  if (!storedSession?.session?.token) return { orders: [] };

  return request("/api/orders/mine", {
    token: storedSession.session.token,
  });
};

export const cancelMyMapOrder = async ({ orderId }) => {
  const storedSession = readStoredSession();
  if (!storedSession?.session?.token) {
    throw new Error("Bitte zuerst anmelden.");
  }

  return request(`/api/orders/mine/${orderId}/cancel`, {
    method: "PATCH",
    token: storedSession.session.token,
  });
};

export const uploadMyMapOrderSchematicZip = async ({ orderId, file }) => {
  const formData = new FormData();
  formData.append("file", file);
  const response = await authenticatedFetch(`/api/orders/mine/${orderId}/files/schematic`, {
    method: "POST",
    body: formData,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || "Schematic-ZIP konnte nicht hochgeladen werden.");
  }
  return data;
};

export const uploadMyMapOrderSourceImage = async ({ orderId, file }) => {
  const formData = new FormData();
  formData.append("file", file);
  const response = await authenticatedFetch(`/api/orders/mine/${orderId}/files/image`, {
    method: "POST",
    body: formData,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || "Bild konnte nicht hochgeladen werden.");
  }
  return data;
};

export const downloadMyMapOrderSchematicZip = async ({ orderId, filename }) => {
  const response = await authenticatedFetch(`/api/orders/mine/${orderId}/files/schematic`);
  if (!response.ok) throw new Error("Schematic-ZIP konnte nicht heruntergeladen werden.");
  const blob = await response.blob();
  downloadBlob(blob, filename || "schematic.zip");
};

export const downloadMyMapOrderSourceImage = async ({ orderId, filename }) => {
  const response = await authenticatedFetch(`/api/orders/mine/${orderId}/files/image`);
  if (!response.ok) throw new Error("Bild konnte nicht heruntergeladen werden.");
  const blob = await response.blob();
  downloadBlob(blob, filename || "mapart-bild.png");
};

export const downloadMapCompanyOrderSchematicZip = async ({ slug, orderId, filename }) => {
  const response = await authenticatedFetch(`/api/orders/organizations/${slug}/${orderId}/files/schematic`);
  if (!response.ok) throw new Error("Schematic-ZIP konnte nicht heruntergeladen werden.");
  const blob = await response.blob();
  downloadBlob(blob, filename || "schematic.zip");
};

export const downloadMapCompanyOrderSourceImage = async ({ slug, orderId, filename }) => {
  const response = await authenticatedFetch(`/api/orders/organizations/${slug}/${orderId}/files/image`);
  if (!response.ok) throw new Error("Bild konnte nicht heruntergeladen werden.");
  const blob = await response.blob();
  downloadBlob(blob, filename || "mapart-bild.png");
};

const downloadBlob = (blob, filename) => {
  const downloadUrl = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = downloadUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  window.URL.revokeObjectURL(downloadUrl);
  document.body.removeChild(anchor);
};

export const listMapCompanyOrders = async ({ slug }) => {
  const storedSession = readStoredSession();
  if (!storedSession?.session?.token) {
    throw new Error("Bitte zuerst anmelden.");
  }

  return request(`/api/orders/organizations/${slug}`, {
    token: storedSession.session.token,
  });
};

export const updateMapCompanyOrderStatus = async ({ slug, orderId, status, decisionNote, completedMapCount }) => {
  const storedSession = readStoredSession();
  if (!storedSession?.session?.token) {
    throw new Error("Bitte zuerst anmelden.");
  }

  return request(`/api/orders/organizations/${slug}/${orderId}/status`, {
    method: "PATCH",
    token: storedSession.session.token,
    body: { status, decisionNote, completedMapCount },
  });
};

export const updateMapCompanyOrderPayment = async ({ slug, orderId, paymentConfirmed }) => {
  const storedSession = readStoredSession();
  if (!storedSession?.session?.token) {
    throw new Error("Bitte zuerst anmelden.");
  }

  return request(`/api/orders/organizations/${slug}/${orderId}/payment`, {
    method: "PATCH",
    token: storedSession.session.token,
    body: { paymentConfirmed },
  });
};

export const updateMapCompanyOrderDiscount = async ({ slug, orderId, manualDiscountPerMapPart, manualDiscountNote }) => {
  const storedSession = readStoredSession();
  if (!storedSession?.session?.token) {
    throw new Error("Bitte zuerst anmelden.");
  }

  return request(`/api/orders/organizations/${slug}/${orderId}/discount`, {
    method: "PATCH",
    token: storedSession.session.token,
    body: { manualDiscountPerMapPart, manualDiscountNote },
  });
};
